import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Truck } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { Button, Card, cx, Input } from "@/components/ui";
import { api, ApiError, type ShopMethodMapping } from "@/lib/api";
import { fmtNumber } from "@/lib/format";

import { t, msg } from "@/lib/i18n";
type Row = ShopMethodMapping["mappings"][number] & { key: string };
const key = () => Math.random().toString(36).slice(2, 9);

/**
 * Zuordnung Versandart des Kanals (Shopify, Odoo, WooCommerce …) → FFN-Versandart je Kanal.
 * „Fest“ gilt immer (Regeln/Optimierung ändern nichts), „Vorschlag“ ist der Startwert.
 */
export function ShippingMappingCard({ channelId, mapping, seen: seenInOrders, channelMethods, channelMethodsError, editable, sourceLabel = "Shop" }: { channelId: string; mapping: ShopMethodMapping; seen: { name: string; count: number }[]; /** im Kanal angelegte Versandmethoden (z. B. aus Odoo), auch ohne bisherigen Auftrag */ channelMethods?: string[]; channelMethodsError?: string; editable: boolean; /** Name des Kanals, z. B. „Odoo“ */ sourceLabel?: string }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const methodsQ = useQuery({ queryKey: ["shipping-methods", tenantId], queryFn: () => api.backend.shippingMethods(tenantId), staleTime: 5 * 60_000, retry: false });
  // Versandarten aller aktiven Lager (ohne Dopplungen)
  const methods = useMemo(() => {
    const out = new Map<string, { id: string; name: string; carrier: string | null }>();
    for (const w of methodsQ.data?.warehouses ?? []) if (w.active || w.primary) for (const m of w.shippingMethods) out.set(m.id, { id: m.id, name: m.name, carrier: m.carrier });
    return [...out.values()].sort((a, b) => a.name.localeCompare(b.name, "de"));
  }, [methodsQ.data]);
  const nameOf = (id: string) => methods.find((m) => m.id === id)?.name ?? id;

  const [rows, setRows] = useState<Row[]>(() => mapping.mappings.map((m) => ({ ...m, key: key() })));
  const [fallback, setFallback] = useState<string>(mapping.fallbackMethodId ?? "");
  const dirty = JSON.stringify(rows.map(({ key: _k, ...r }) => r)) !== JSON.stringify(mapping.mappings) || (fallback || null) !== mapping.fallbackMethodId;
  // Vorschläge: im Kanal angelegte Methoden (Reihenfolge des Kanals) + in Aufträgen vorgekommene, mit Anzahl
  const seen = useMemo(() => {
    const count = new Map(seenInOrders.map((s) => [s.name.trim().toLowerCase(), s]));
    const fromChannel = (channelMethods ?? []).map((name) => ({ name, count: count.get(name.trim().toLowerCase())?.count ?? 0 }));
    const known = new Set(fromChannel.map((m) => m.name.trim().toLowerCase()));
    return [...fromChannel, ...seenInOrders.filter((s) => !known.has(s.name.trim().toLowerCase()))];
  }, [seenInOrders, channelMethods]);
  const mapped = new Set(rows.map((r) => r.shopMethod.trim().toLowerCase()));
  const unmapped = seen.filter((s) => !mapped.has(s.name.trim().toLowerCase()));

  const save = useMutation({
    mutationFn: () => api.channels.setShippingMapping(tenantId, channelId, { mappings: rows.filter((r) => r.shopMethod.trim() && r.methodId).map(({ key: _k, ...r }) => ({ ...r, shopMethod: r.shopMethod.trim() })), fallbackMethodId: fallback || null }),
    onSuccess: () => {
      toast.success("Versandarten-Zuordnung gespeichert", { description: msg("Gilt für neue Aufträge und beim erneuten Prüfen.") });
      void qc.invalidateQueries({ queryKey: ["channel", tenantId, channelId] });
    },
    onError: (e) => toast.error("Nicht gespeichert", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });
  const update = (k: string, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === k ? { ...r, ...patch } : r)));
  const add = (shopMethod = "") => setRows((rs) => [...rs, { key: key(), shopMethod, methodId: "", mode: "preferred" }]);

  const select = (value: string, onChange: (v: string) => void, placeholder: string, label: string) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={!editable} aria-label={label} className="h-9 w-full rounded-lg border border-border bg-surface px-2 text-[13px]">
      <option value="">{t(placeholder)}</option>
      {methods.map((m) => <option key={m.id} value={m.id}>{m.name}{m.carrier ? ` · ${m.carrier}` : ""}</option>)}
      {value && !methods.some((m) => m.id === value) && <option value={value}>{value} {t("(nicht mehr im Lager)")}</option>}
    </select>
  );

  return (
    <Card className="p-5">
      <h2 className="font-semibold text-[14px] flex items-center gap-2"><Truck className="size-4 text-fg-muted" /> {t("Versandarten-Zuordnung")}</h2>
      <p className="text-[12.5px] text-fg-muted mt-0.5">
        {t("Welche Lager-Versandart gilt für die in")} {sourceLabel} {t("gewählte Versandart?")} <b className="text-fg">{t("Fest")}</b> {t("gilt immer – Regeln und Optimierung ändern nichts.")} <b className="text-fg">{t("Vorschlag")}</b> {t("ist der Startwert; Versandregeln und die Logiful-Optimierung dürfen ihn ändern.")}
      </p>

      {channelMethodsError && <p className="mt-3 text-[12.5px] text-warning">{t("Versandmethoden aus")} {sourceLabel} {t("nicht geladen (")}{channelMethodsError}{t(") – Vorschläge nur aus bisherigen Aufträgen.")}</p>}
      {methodsQ.isError && <p className="mt-3 text-[12.5px] text-danger">{t("Lager-Versandarten konnten nicht geladen werden – ist das Lager verbunden?")}</p>}

      {rows.length > 0 && (
        <div className="mt-3 space-y-2">
          <div className="hidden sm:grid grid-cols-[1fr_1fr_auto_auto] gap-2 text-[11.5px] font-medium text-fg-subtle px-0.5">
            <span>{sourceLabel}{t("-Versandart")}</span><span>{t("Lager-Versandart (FFN)")}</span><span className="w-[148px]">{t("Gilt")}</span><span className="w-8" />
          </div>
          {rows.map((r) => (
            <div key={r.key} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-2 items-center">
              <Input value={r.shopMethod} onChange={(e) => update(r.key, { shopMethod: e.target.value })} placeholder={t("z. B. Standardversand")} list={`seen-${channelId}`} disabled={!editable} aria-label={t("{sourceLabel}-Versandart", { sourceLabel })} />
              {select(r.methodId, (v) => update(r.key, { methodId: v }), "Versandart wählen …", "Lager-Versandart")}
              <div className="inline-flex rounded-lg border border-border p-0.5 w-[148px]" role="group" aria-label={t("Gilt")}>
                {(["preferred", "fixed"] as const).map((mode) => (
                  <button key={mode} type="button" disabled={!editable} onClick={() => update(r.key, { mode })} aria-pressed={r.mode === mode} className={cx("flex-1 h-8 rounded-md text-[12.5px]", r.mode === mode ? "bg-ink text-ink-fg font-medium" : "text-fg-muted hover:bg-surface-2")}>
                    {mode === "fixed" ? t("Fest") : t("Vorschlag")}
                  </button>
                ))}
              </div>
              {editable && (
                <button onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} className="size-8 grid place-items-center rounded-lg text-fg-subtle hover:text-danger hover:bg-surface-2" aria-label={t("Zuordnung entfernen")}>
                  <Trash2 className="size-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <datalist id={`seen-${channelId}`}>{seen.map((s) => <option key={s.name} value={s.name} />)}</datalist>

      {editable && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => add()}>{t("Zuordnung")}</Button>
          {unmapped.length > 0 && <span className="text-[12px] text-fg-subtle ml-1">{t("Noch nicht zugeordnet:")}</span>}
          {unmapped.slice(0, 16).map((s) => (
            <button key={s.name} onClick={() => add(s.name)} className="h-7 px-2.5 rounded-lg border border-dashed border-border-strong text-[12px] hover:bg-surface-2" title={s.count ? t("Zuordnung anlegen – in {v} Aufträgen der letzten 180 Tage", { v: fmtNumber(s.count) }) : t("Zuordnung anlegen – in {sourceLabel} angelegt, noch kein Auftrag", { sourceLabel })}>
              {s.name} <span className="text-fg-subtle tabular-nums">· {s.count ? fmtNumber(s.count) : t("neu")}</span>
            </button>
          ))}
          {unmapped.length > 16 && <span className="text-[12px] text-fg-subtle">+{unmapped.length - 16} {t("weitere (im Feld vorschlagbar)")}</span>}
        </div>
      )}

      <label className="mt-4 grid sm:grid-cols-[1fr_1fr] gap-2 items-center text-[13px]">
        <span>{t("Sonst (nicht zugeordnete")} {sourceLabel}{t("-Versandarten)")} <span className="text-fg-subtle">{t("– als Vorschlag")}</span></span>
        {select(fallback, setFallback, "keine – Versandregeln/Standard", "Lager-Versandart für nicht zugeordnete")}
      </label>

      {editable && (
        <div className="mt-4 flex items-center justify-end gap-2">
          {dirty && <span className="text-[12px] text-fg-muted">{t("Ungespeicherte Änderungen")}</span>}
          <Button variant="primary" size="sm" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button>
        </div>
      )}
      {!editable && rows.length === 0 && <p className="mt-3 text-[12.5px] text-fg-muted">{t("Noch keine Zuordnung – Aufträge gehen mit")} {mapping.fallbackMethodId ? nameOf(mapping.fallbackMethodId) : t("der Standard-Versandart")} {t("ans Lager.")}</p>}
    </Card>
  );
}
